import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Loader2, ExternalLink, TrendingUp, Clock, ToggleLeft, ToggleRight, BarChart2, ShieldCheck, Database, GitBranch } from 'lucide-react';
import { RoutingRulesEditor } from './RoutingRulesEditor';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatScanCount, getRedirectUrl } from '@/lib/dynamic-qr-utils';

interface QRCodeWithDynamic {
  id: string;
  name: string;
  type: string;
  contentData: Record<string, string>;
  shortSlug?: string;
  isActive?: boolean;
  clicksCount?: number;
  lastClickedAt?: string;
  updatedAt?: string;
  analyticsConfig?: {
    ga4_id?: string;
    fb_pixel_id?: string;
    google_ads_id?: string;
    custom_script?: string;
  };
  blockchainConfig?: {
    enabled: boolean;
    network: string;
    collection_name?: string;
    mint_on_scan: boolean;
    nft_gating?: {
      enabled: boolean;
      contract_address: string;
      min_balance: number;
    };
    nft_metadata?: {
      name: string;
      description: string;
      image: string;
    };
  };
  routingRules: Array<{
    id: string;
    type: 'time' | 'os' | 'device' | 'country' | 'language';
    condition: Record<string, unknown>;
    destination: string;
  }>;
}

interface EditDynamicQRModalProps {
  qr: QRCodeWithDynamic | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (id: string, updates: { 
    contentData: Record<string, string>; 
    isActive: boolean; 
    analyticsConfig?: any; 
    blockchainConfig?: any;
    routingRules?: any[];
  }) => Promise<void>;
}

export function EditDynamicQRModal({ qr, open, onOpenChange, onSave }: EditDynamicQRModalProps) {
  const [contentData, setContentData] = useState<Record<string, string>>({});
  const [isActive, setIsActive] = useState(true);
  const [analyticsConfig, setAnalyticsConfig] = useState<any>({});
  const [blockchainConfig, setBlockchainConfig] = useState<any>({});
  const [routingRules, setRoutingRules] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (qr) {
      setContentData(qr.contentData || {});
      setIsActive(qr.isActive ?? true);
      setRoutingRules(qr.routingRules || []);
      setAnalyticsConfig(qr.analyticsConfig || {
        ga4_id: '',
        fb_pixel_id: '',
        google_ads_id: '',
        custom_script: ''
      });
      setBlockchainConfig(qr.blockchainConfig || {
        enabled: false,
        network: 'polygon-amoy',
        collection_name: '',
        mint_on_scan: false,
        nft_gating: {
          enabled: false,
          contract_address: '',
          min_balance: 1
        },
        nft_metadata: {
          name: 'NexQR Verified Scan',
          description: 'Proof of physical interaction via NexQR',
          image: 'https://nexqr.online/nft-placeholder.png'
        }
      });
    }
  }, [qr]);

  const handleSave = async () => {
    if (!qr) return;
    
    setIsSaving(true);
    try {
      await onSave(qr.id, { contentData, isActive, analyticsConfig, blockchainConfig, routingRules });
      toast.success('QR updated! No need to reprint.');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to update QR code');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: string, value: string) => {
    setContentData(prev => ({ ...prev, [field]: value }));
  };

  if (!qr) return null;

  const redirectUrl = qr.shortSlug ? getRedirectUrl(qr.shortSlug) : '';

  // Determine editable fields based on QR type
  const getEditableFields = () => {
    switch (qr.type) {
      case 'url':
        return [{ name: 'url', label: 'Destination URL', type: 'url' }];
      case 'whatsapp':
        return [
          { name: 'phone', label: 'Phone Number', type: 'tel' },
          { name: 'message', label: 'Message', type: 'textarea' },
        ];
      case 'phone':
        return [{ name: 'phone', label: 'Phone Number', type: 'tel' }];
      case 'sms':
        return [
          { name: 'phone', label: 'Phone Number', type: 'tel' },
          { name: 'message', label: 'Message', type: 'textarea' },
        ];
      case 'email':
        return [
          { name: 'email', label: 'Email Address', type: 'email' },
          { name: 'subject', label: 'Subject', type: 'text' },
          { name: 'body', label: 'Body', type: 'textarea' },
        ];
      case 'instagram':
      case 'twitter':
      case 'linkedin':
        return [{ name: 'username', label: 'Username', type: 'text' }];
      case 'paypal':
        return [
          { name: 'username', label: 'PayPal.me Username', type: 'text' },
          { name: 'amount', label: 'Amount', type: 'number' },
        ];
      case 'pdf':
      case 'appstore':
        return [{ name: 'url', label: qr.type === 'pdf' ? 'PDF URL' : 'App Store URL', type: 'url' }];
      case 'bitcoin':
        return [
          { name: 'address', label: 'Crypto Address', type: 'text' },
          { name: 'amount', label: 'Amount', type: 'text' },
        ];
      case 'wifi':
        return [
          { name: 'ssid', label: 'Network Name (SSID)', type: 'text' },
          { name: 'password', label: 'Password', type: 'text' },
          { name: 'encryption', label: 'Encryption', type: 'text' },
        ];
      case 'text':
        return [{ name: 'text', label: 'Text Content', type: 'textarea' }];
      case 'calendar':
        return [
          { name: 'title', label: 'Event Title', type: 'text' },
          { name: 'startDate', label: 'Start Date', type: 'text' },
          { name: 'endDate', label: 'End Date', type: 'text' },
          { name: 'location', label: 'Location', type: 'text' },
          { name: 'description', label: 'Description', type: 'textarea' },
        ];
      case 'location':
        return [
          { name: 'latitude', label: 'Latitude', type: 'text' },
          { name: 'longitude', label: 'Longitude', type: 'text' },
          { name: 'label', label: 'Label', type: 'text' },
        ];
      case 'vcard':
        return [
          { name: 'firstName', label: 'First Name', type: 'text' },
          { name: 'lastName', label: 'Last Name', type: 'text' },
          { name: 'organization', label: 'Organization', type: 'text' },
          { name: 'title', label: 'Job Title', type: 'text' },
          { name: 'phone', label: 'Phone', type: 'tel' },
          { name: 'email', label: 'Email', type: 'email' },
          { name: 'website', label: 'Website', type: 'url' },
        ];
      default:
        return Object.keys(contentData).map(key => ({
          name: key,
          label: key.charAt(0).toUpperCase() + key.slice(1),
          type: 'text',
        }));
    }
  };

  const editableFields = getEditableFields();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit Dynamic QR
            <Badge variant="secondary">Dynamic</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <Tabs defaultValue="content" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="routing" className="flex items-center gap-2">
                <GitBranch className="h-4 w-4" />
                Routing
              </TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-2">
                <BarChart2 className="h-4 w-4" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="blockchain" className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                Web3
              </TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="space-y-6 mt-4">
              {/* Stats Section */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-2xl font-bold">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    {formatScanCount(qr.clicksCount || 0)}
                  </div>
                  <p className="text-xs text-muted-foreground">Total Scans</p>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium">
                    {qr.lastClickedAt 
                      ? format(new Date(qr.lastClickedAt), 'MMM d, HH:mm')
                      : 'Never'
                    }
                  </div>
                  <p className="text-xs text-muted-foreground">Last Scan</p>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium">
                    {qr.updatedAt 
                      ? format(new Date(qr.updatedAt), 'MMM d')
                      : '-'
                    }
                  </div>
                  <p className="text-xs text-muted-foreground">Last Modified</p>
                </div>
              </div>

              {/* Short URL Display */}
              {redirectUrl && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Short URL (unchangeable)</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={redirectUrl} 
                      readOnly 
                      className="bg-muted text-xs font-mono"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(redirectUrl, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <Separator />

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="space-y-1">
                  <Label className="font-medium flex items-center gap-2">
                    {isActive ? (
                      <ToggleRight className="h-5 w-5 text-primary" />
                    ) : (
                      <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                    )}
                    QR Code Status
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {isActive 
                      ? 'Active - redirects to destination' 
                      : 'Paused - shows disabled page'
                    }
                  </p>
                </div>
                <Switch
                  checked={isActive}
                  onCheckedChange={(checked) => {
                    if (!checked) {
                      if (window.confirm('Deactivating this QR code will show a "paused" page to anyone who scans it. Continue?')) {
                        setIsActive(false);
                      }
                    } else {
                      setIsActive(true);
                    }
                  }}
                />
              </div>

              {/* Editable Fields */}
              <div className="space-y-4">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                  Destination Content
                </Label>
                {editableFields.map((field) => (
                  <div key={field.name} className="space-y-2">
                    <Label htmlFor={field.name}>{field.label}</Label>
                    {field.type === 'textarea' ? (
                      <textarea
                        id={field.name}
                        value={contentData[field.name] || ''}
                        onChange={(e) => updateField(field.name, e.target.value)}
                        className="w-full min-h-[80px] px-3 py-2 border rounded-md bg-background resize-none"
                      />
                    ) : (
                      <Input
                        id={field.name}
                        type={field.type}
                        value={contentData[field.name] || ''}
                        onChange={(e) => updateField(field.name, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="routing" className="space-y-6 mt-4">
              <RoutingRulesEditor 
                rules={routingRules}
                onChange={setRoutingRules}
              />
            </TabsContent>

            <TabsContent value="analytics" className="space-y-6 mt-4">
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <BarChart2 className="h-4 w-4 text-primary" />
                  Analytics Forwarding
                </h4>
                <p className="text-xs text-muted-foreground">
                  Track scans directly in your favorite analytics tools. Scripts are injected when users scan this QR.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ga4">Google Analytics 4 ID</Label>
                  <Input 
                    id="ga4"
                    placeholder="G-XXXXXXXXXX" 
                    value={analyticsConfig.ga4_id || ''}
                    onChange={(e) => setAnalyticsConfig({ ...analyticsConfig, ga4_id: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fb-pixel">Facebook Pixel ID</Label>
                  <Input 
                    id="fb-pixel"
                    placeholder="1234567890..." 
                    value={analyticsConfig.fb_pixel_id || ''}
                    onChange={(e) => setAnalyticsConfig({ ...analyticsConfig, fb_pixel_id: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom-script">Custom Script (JS)</Label>
                  <textarea
                    id="custom-script"
                    placeholder="console.log('QR Scanned');"
                    value={analyticsConfig.custom_script || ''}
                    onChange={(e) => setAnalyticsConfig({ ...analyticsConfig, custom_script: e.target.value })}
                    className="w-full min-h-[100px] px-3 py-2 border rounded-md bg-background font-mono text-xs resize-none"
                  />
                  <p className="text-[10px] text-muted-foreground italic">
                    Enter raw JavaScript code. It will be wrapped in &lt;script&gt; tags.
                  </p>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="blockchain" className="space-y-6 mt-4">
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-primary">
                  <ShieldCheck className="h-4 w-4" />
                  Blockchain Authenticity
                </h4>
                <p className="text-xs text-muted-foreground">
                  Secure your QR codes with NFT-backed proof of scan. Users receive a digital collectible on interaction.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Enable NFT Minting</Label>
                    <p className="text-[10px] text-muted-foreground">Mint proof-of-scan NFT on every unique scan</p>
                  </div>
                  <Switch 
                    checked={blockchainConfig.enabled}
                    onCheckedChange={(v) => setBlockchainConfig({ ...blockchainConfig, enabled: v })}
                  />
                </div>

                {blockchainConfig.enabled && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4 pt-2"
                  >
                    <div className="space-y-2">
                      <Label>Network</Label>
                      <Select 
                        value={blockchainConfig.network}
                        onValueChange={(v) => setBlockchainConfig({ ...blockchainConfig, network: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="polygon-amoy">Polygon Amoy (Testnet)</SelectItem>
                          <SelectItem value="polygon-mainnet">Polygon Mainnet</SelectItem>
                          <SelectItem value="base">Base</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="collection">Collection Name</Label>
                      <Input 
                        id="collection"
                        placeholder="NexQR Verified Scans" 
                        value={blockchainConfig.collection_name || ''}
                        onChange={(e) => setBlockchainConfig({ ...blockchainConfig, collection_name: e.target.value })}
                      />
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-medium">NFT Gating</Label>
                          <p className="text-[10px] text-muted-foreground">Restrict access to holders of a specific NFT</p>
                        </div>
                        <Switch 
                          checked={blockchainConfig.nft_gating?.enabled}
                          onCheckedChange={(v) => setBlockchainConfig({ 
                            ...blockchainConfig, 
                            nft_gating: { ...blockchainConfig.nft_gating, enabled: v } 
                          })}
                        />
                      </div>

                      {blockchainConfig.nft_gating?.enabled && (
                        <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                          <div className="space-y-2">
                            <Label htmlFor="contract" className="text-xs">Contract Address</Label>
                            <Input 
                              id="contract"
                              placeholder="0x..." 
                              className="h-8 text-xs font-mono"
                              value={blockchainConfig.nft_gating?.contract_address || ''}
                              onChange={(e) => setBlockchainConfig({ 
                                ...blockchainConfig, 
                                nft_gating: { ...blockchainConfig.nft_gating, contract_address: e.target.value } 
                              })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="balance" className="text-xs">Min Balance</Label>
                            <Input 
                              id="balance"
                              type="number"
                              className="h-8 text-xs"
                              value={blockchainConfig.nft_gating?.min_balance || 1}
                              onChange={(e) => setBlockchainConfig({ 
                                ...blockchainConfig, 
                                nft_gating: { ...blockchainConfig.nft_gating, min_balance: parseInt(e.target.value) } 
                              })}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <Separator />
                    
                    <div className="space-y-3">
                      <Label className="text-xs uppercase font-bold text-muted-foreground">NFT Metadata</Label>
                      
                      <div className="space-y-2">
                        <Label htmlFor="nft-name" className="text-xs">Asset Name</Label>
                        <Input 
                          id="nft-name"
                          className="h-8 text-xs"
                          value={blockchainConfig.nft_metadata?.name || ''}
                          onChange={(e) => setBlockchainConfig({ 
                            ...blockchainConfig, 
                            nft_metadata: { ...blockchainConfig.nft_metadata, name: e.target.value } 
                          })}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="nft-desc" className="text-xs">Description</Label>
                        <textarea
                          id="nft-desc"
                          className="w-full min-h-[60px] px-3 py-2 border rounded-md bg-background text-xs resize-none"
                          value={blockchainConfig.nft_metadata?.description || ''}
                          onChange={(e) => setBlockchainConfig({ 
                            ...blockchainConfig, 
                            nft_metadata: { ...blockchainConfig.nft_metadata, description: e.target.value } 
                          })}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
